import {
  Home,
  CalendarRange,
  BookCheck,
  Users,
  Sparkles,
  ShoppingBag,
  LayoutGrid,
  Calendar,
  Quote,
  Package2Icon,
  FolderTree,
  Image as ImageIcon,
  MessageSquare,
  BookOpen,
  TableOfContents,
  PenSquare,
  type LucideIcon,
} from "lucide-react";

/**
 * Single source of truth for the admin information architecture.
 *
 * Ordered by usage frequency (daily → monthly), capped at seven primary
 * destinations. Settings is intentionally NOT in this list — it is pinned
 * separately at the foot of the sidebar because it is set-and-forget
 * configuration, not a day-to-day operation.
 *
 * `children` render as a single level of secondary nav inside the sidebar
 * (auto-expanded when the section is active). Anything deeper lives inside the
 * section's own page as tabs (e.g. Schedule), never nested further here.
 */

export interface NavChild {
  name: string;
  href: string;
  icon: LucideIcon;
}

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /** When true, only an exact path match counts as active (the Dashboard root). */
  exact?: boolean;
  /** Extra path prefixes that should also light this item up. */
  alsoMatch?: string[];
  children?: NavChild[];
}

export const primaryNav: NavItem[] = [
  { name: "Dashboard", href: "/control", icon: Home, exact: true },
  {
    name: "Schedule",
    href: "/control/schedule",
    icon: CalendarRange,
  },
  {
    name: "Bookings",
    href: "/control/bookings",
    icon: BookCheck,
    alsoMatch: ["/control/quotes"],
    children: [
      { name: "All bookings", href: "/control/bookings", icon: Calendar },
      { name: "Quote requests", href: "/control/quotes", icon: Quote },
    ],
  },
  { name: "Customers", href: "/control/customers", icon: Users },
  { name: "Services", href: "/control/services", icon: Sparkles },
  {
    name: "Shop",
    href: "/control/shop",
    icon: ShoppingBag,
    alsoMatch: ["/control/inventory"],
    children: [
      { name: "Orders", href: "/control/shop", icon: ShoppingBag },
      { name: "Catalogue", href: "/control/inventory", icon: Package2Icon },
    ],
  },
  {
    name: "Content",
    href: "/control/cms/gallery",
    icon: LayoutGrid,
    alsoMatch: ["/control/cms"],
    children: [
      { name: "Gallery", href: "/control/cms/gallery", icon: ImageIcon },
      { name: "Reviews", href: "/control/cms/reviews", icon: MessageSquare },
      { name: "Our Story", href: "/control/cms/our-story", icon: BookOpen },
      { name: "Footer", href: "/control/cms/footer-content", icon: TableOfContents },
      { name: "Privacy Policy", href: "/control/cms/privacy-policy", icon: PenSquare },
    ],
  },
];

/**
 * The three daily-use surfaces, surfaced as a mobile bottom tab bar. A fourth
 * "More" tab (added in the component) opens the full drawer.
 */
export const mobilePrimary: NavItem[] = primaryNav.filter((item) =>
  ["Dashboard", "Schedule", "Bookings"].includes(item.name)
);

/** Decide whether a nav href is the current location. */
export function isNavActive(
  currentPath: string,
  item: { href: string; exact?: boolean; alsoMatch?: string[] }
): boolean {
  if (item.exact) return currentPath === item.href;
  if (currentPath === item.href || currentPath.startsWith(item.href + "/")) return true;
  return (item.alsoMatch ?? []).some(
    (p) => currentPath === p || currentPath.startsWith(p + "/") || currentPath.startsWith(p)
  );
}
